import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {CacheIcon, LockIcon, MeterIcon, RocketIcon, ServerIcon, VerifiedIcon, type Icon} from '@primer/octicons-react'
import {useMemo} from 'react'
import {SidebarNavItem, type SidebarAnalyticsKey} from './SidebarNavItem'
import type {WorkflowSidebarSelection} from './WorkflowSidebar'
import styles from './WorkflowSidebar.module.css'

interface ManagementItem {
  analyticsKey: SidebarAnalyticsKey
  label: string
  href: string
  icon: Icon
  selection?: WorkflowSidebarSelection['type']
  softNavigationEnabled?: boolean
  external?: boolean
}

export interface WorkflowSidebarFooterProps {
  owner: string
  repo: string
  softNavigationEnabled: boolean
  showDeployments: boolean
  showAttestations: boolean
  showRunners: boolean
  lockfilePath?: string | null
  showUsageMetrics: boolean
  selection: WorkflowSidebarSelection
}

export function WorkflowSidebarFooter({
  owner,
  repo,
  softNavigationEnabled,
  showDeployments,
  showAttestations,
  showRunners,
  lockfilePath,
  showUsageMetrics,
  selection,
}: WorkflowSidebarFooterProps) {
  const managementItems = useMemo<ManagementItem[]>(() => {
    const nativeCachesEnabled = isFeatureEnabled('actions_caches_react_view')
    const items: ManagementItem[] = [
      {
        analyticsKey: 'caches',
        label: 'Caches',
        href: `/${owner}/${repo}/actions/caches`,
        icon: CacheIcon,
        selection: 'caches',
        softNavigationEnabled: softNavigationEnabled && nativeCachesEnabled,
      },
    ]
    if (showDeployments) {
      items.push({
        analyticsKey: 'deployments',
        label: 'Deployments',
        href: `/${owner}/${repo}/deployments`,
        icon: RocketIcon,
        external: true,
      })
    }
    if (showAttestations) {
      items.push({
        analyticsKey: 'attestations',
        label: 'Attestations',
        href: `/${owner}/${repo}/attestations`,
        icon: VerifiedIcon,
        external: true,
      })
    }
    if (showRunners) {
      const nativeRunnersEnabled = isFeatureEnabled('actions_runners_react_view')
      items.push({
        analyticsKey: 'runners',
        label: 'Runners',
        href: `/${owner}/${repo}/actions/runners`,
        icon: ServerIcon,
        selection: 'runners',
        softNavigationEnabled: softNavigationEnabled && nativeRunnersEnabled,
      })
    }
    if (lockfilePath) {
      items.push({analyticsKey: 'lockfile', label: 'Lockfile', href: lockfilePath, icon: LockIcon, external: true})
    }
    if (showUsageMetrics) {
      items.push(
        {
          analyticsKey: 'usage',
          label: 'Usage metrics',
          href: `/${owner}/${repo}/actions/metrics/usage`,
          icon: MeterIcon,
          external: true,
        },
        {
          analyticsKey: 'performance',
          label: 'Performance metrics',
          href: `/${owner}/${repo}/actions/metrics/performance`,
          icon: MeterIcon,
          external: true,
        },
      )
    }
    return items
  }, [
    lockfilePath,
    owner,
    repo,
    showAttestations,
    showDeployments,
    showRunners,
    showUsageMetrics,
    softNavigationEnabled,
  ])

  return (
    <div className={styles.managementFooter}>
      <CollapsibleNavList aria-label="Actions management" className={styles.managementNav}>
        {managementItems.map(item => (
          <SidebarNavItem
            analyticsKey={item.analyticsKey}
            key={item.label}
            label={item.label}
            href={item.href}
            icon={item.icon}
            isCurrent={item.selection === selection.type}
            softNavigationEnabled={item.softNavigationEnabled}
            data-turbo={item.selection === 'caches' || item.selection === 'runners' ? 'false' : undefined}
            external={item.external}
          />
        ))}
      </CollapsibleNavList>
      <CollapsibleSidebar.Toggle />
    </div>
  )
}

WorkflowSidebarFooter.displayName = 'WorkflowSidebarFooter'
